import React, { useEffect, useState } from 'react';
// Import the fetchResults function from the local provider API
import { fetchResults, ResultsData } from '../api/provider';
import { formatBirthdate } from './utils/dashboardHelpers';

interface KitResultsProps {
  serialNumber: string;
  onClose: () => void;
}

// --- Helper Component for Checkboxes ---
interface ResultCheckboxProps {
  label: string;
  checked: boolean | undefined;
}

const ResultCheckbox: React.FC<ResultCheckboxProps> = ({ label, checked }) => (
  <div className="relative flex gap-x-3 mb-2 items-center">
    <div className="flex h-6 items-center">
      <input
        type="checkbox"
        readOnly
        checked={!!checked}
        className={`h-6 w-6 rounded border-[#45A3DB] text-[#45A3DB] focus:ring-0 cursor-default ${checked ? 'checked-box' : ''}`}
        style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
      />
    </div>
    <div className="text-sm leading-6">
      <label>{label}</label>
    </div>
  </div>
);

// --- Helper function for comments ---
const getCommentForResult = (result: string | undefined): string => {
  switch (result) {
    case 'notFertile':
    case 'notFertileNoSperm':
      return 'A well-mixed uncentrifuged post vasectomy semen sample showed no sperm. Based on AUA Vasectomy Guidelines - your vasectomy procedure appears to have been successful.';
    case 'notFertileNonMotile':
      return 'A well-mixed uncentrifuged fresh post vasectomy semen showed only rare non-motile sperm (≤ 100,000 sperm/ml). Based on AUA Vasectomy Guidelines - your vasectomy procedure appears to have been successful.';
    case 'fertile':
      return 'Sperm concentration (count) could be determined on a counting chamber. If it has been more than 12 weeks since the date of your vasectomy procedure it is recommended that you schedule a follow up with the clinic that performed your vasectomy procedure.';
    case 'questionable':
      return 'One well-mixed uncentrifuged fresh post vasectomy semen shows greater than 100,000 non-motile sperm/ml. If it has been more than 12 weeks since the date of your vasectomy procedure, it is recommended that you schedule a follow up with the clinic that performed your vasectomy procedure.';
    case 'error':
      return 'Your sample was deemed ineligible by our lab for proper post vasectomy analysis due to the condition it was received or failure to follow instructions. Customer support has been notified and will be reaching out to you within the next 24 hours. If you do not hear from Customer Support in the next 24 hours please reach out to determine next steps.';
    default:
      return 'No comments available.';
  }
};

// --- Date formatting helpers ---
const formatProcedureDate = (date: string | null | undefined): string => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString(navigator.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatCollectionDate = (dateTime: string | null | undefined): string => {
  if (!dateTime) return 'N/A';
  return new Date(dateTime).toLocaleDateString(navigator.language);
};

const formatCollectionTime = (dateTime: string | null | undefined): string => {
  if (!dateTime) return 'N/A';
  return new Date(dateTime).toLocaleTimeString(navigator.language);
};

const formatLabDate = (timestamp: number | null | undefined): string => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp * 1000).toLocaleDateString(navigator.language);
};

const formatLabTime = (timestamp: number | null | undefined): string => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp * 1000).toLocaleTimeString(navigator.language);
};

const getLogoSource = (result: string | undefined): string => {
  return result?.includes("notFertile") 
    ? "../../images/JackFinalGif.gif" 
    : "../../images/jack-logo.png";
};

// --- Print styles constant ---
const PRINT_STYLES = `
  body { 
    font-family: Arial, sans-serif; 
    margin: 0; 
    color: #333; 
    background: #f3f4f6;
    line-height: 1.6;
    padding: 20px;
  }
  .print-results-container { 
    max-width: 800px; 
    margin: 0 auto;
    background: white;
    box-shadow: 0 0 10px rgba(0,0,0,0.1);
    padding: 20px;
  }
  img { 
    max-width: 400px; 
    width: 100%;
    height: auto; 
    margin: 0 auto;
    display: block;
  }
  .grid { 
    display: grid; 
    grid-template-columns: 1fr 1fr; 
    gap: 30px;
    margin-top: 20px;
  }
  .space-y-6 > div {
    margin-bottom: 20px;
  }
  .text-brand-blue-400 { 
    color: #45A3DB; 
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 12px;
    border-bottom: 2px solid #45A3DB;
    padding-bottom: 4px;
  }
  .bg-gray-50 { 
    background: #f9f9f9; 
    padding: 12px; 
    border-radius: 4px;
    border-left: 4px solid #45A3DB;
    font-size: 15px;
    line-height: 1.4;
  }
  .text-xs { 
    font-size: 12px; 
    color: #666; 
    line-height: 1.3;
    text-align: justify;
    border-top: 2px solid #45A3DB;
    padding-top: 15px;
    margin-top: 25px;
  }
  ul { 
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li { 
    margin-bottom: 6px;
    font-size: 15px;
    line-height: 1.3;
  }
  .font-bold, strong { 
    font-weight: bold;
    color: #333;
  }
  .flex { 
    display: flex; 
    align-items: center; 
    gap: 8px;
    padding: 2px;
    background: #f9f9f9;
    font-size: 13px;
  }
  .flex.checked {
    background: #45A3DB;
    border-color: #45A3DB;
  }
  input[type="checkbox"] { 
    width: 20px; 
    height: 20px; 
    accent-color: #45A3DB;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    border: 1px solid #45A3DB;
    border-radius: 4px;
    background-color: white;
    position: relative;
  }
  input[type="checkbox"].checked-box {
    background-color: #45A3DB;
    border-color: #45A3DB;
  }
  input[type="checkbox"].checked-box::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: translate(-50%, -60%) rotate(45deg);
  }
  .pl-28 {
    padding-left: 7rem;
  }
  .mt-10 {
    margin-top: 15px;
  }
  .pt-5 {
    padding-top: 15px;
  }
  .px-8 {
    padding-left: 2rem;
    padding-right: 2rem;
  }
  .pb-8 {
    padding-bottom: 1rem;
  }
  .mb-8 {
    margin-bottom: 20px;
  }
  .py-5 {
    padding-top: 15px;
    padding-bottom: 15px;
  }
  .mt-6 {
    margin-top: 15px;
  }
  .mt-8 {
    margin-top: 20px;
  }
  .space-y-2 > * + * {
    margin-top: 8px;
  }
  .space-y-6 > * + * {
    margin-top: 20px;
  }
  @media print {
    body { 
      background: white;
      padding: 0;
    }
    .print-results-container {
      box-shadow: none;
      padding: 20px;
    }
    .text-xs {
      page-break-inside: avoid;
    }
    .grid {
      page-break-inside: avoid;
    }
    input[type="checkbox"].checked-box {
      background-color: #000 !important;
      border-color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    input[type="checkbox"].checked-box::after {
      border-color: white;
    }
  }
`;

// --- Print function ---
const handlePrint = (): void => {
  try {
    const printContent = document.querySelector('.print-results-container');
    if (!printContent) {
      console.error('Print content not found');
      console.error('Could not find content to print');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      console.error('Failed to open print window - popup blocked?');
      console.error('Print window blocked. Please allow popups for this site.');
      return;
    }

    const doc = printWindow.document;
    const htmlEl = doc.createElement('html');
    htmlEl.innerHTML = `
      <head>
        <title>Test Results</title>
        <style>${PRINT_STYLES}</style>
      </head>
      <body>
        ${printContent.outerHTML}
      </body>
    `;

    // Avoid deprecated document.write and ensure documentElement exists
    doc.open();
    doc.replaceChildren(htmlEl);
    doc.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  } catch (error) {
    console.error('Print error:', error);
    console.error('Error generating PDF. Please try again.');
  }
};

// --- Sub-components ---
const LoadingState: React.FC = () => (
  <div className="flex justify-center items-center py-10">
    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
    <strong className="font-bold">Error: </strong>
    <span className="block sm:inline">{error}</span>
  </div>
);

interface PatientInformationProps {
  data: ResultsData;
}

const PatientInformation: React.FC<PatientInformationProps> = ({ data }) => (
  <div>
    <h3 className="text-brand-blue-400 text-base font-bold mb-4 pb-1 border-b-2 border-brand-blue-400">
      Patient Information:
    </h3>
    <ul className="space-y-2 text-sm leading-relaxed">
      <li><strong>Patient Name:</strong> {data?.customerDetails?.given_name} {data?.customerDetails?.family_name}</li>
      <li><strong>Kit ID#:</strong> {data?.results?.kitId ?? 'N/A'}</li>
      <li><strong>Test Type:</strong> Post-Vasectomy Semen Test</li>
      <li><strong>Vasectomy Date:</strong> {formatProcedureDate(data?.results?.procedureDate)}</li>
      <li><strong>Date of Birth:</strong> {formatBirthdate(data?.customerDetails?.birthdate)}</li>
    </ul>
  </div>
);

interface ResultCheckboxesProps {
  result: string | undefined;
}

const ResultCheckboxes: React.FC<ResultCheckboxesProps> = ({ result }) => (
  <div>
    <h3 className="text-brand-blue-400 text-base font-bold mb-4 pb-1 border-b-2 border-brand-blue-400">
      Result:
    </h3>
    <div className="space-y-2">
      <ResultCheckbox
        label="Negative (No Sperm Detected)"
        checked={result === 'notFertile' || result === 'notFertileNoSperm'}
      />
      <ResultCheckbox
        label="Negative (Non-motile Sperm Detected)"
        checked={result === 'notFertileNonMotile'}
      />
      <ResultCheckbox
        label="Positive (Sperm Detected) - Patient Follow-up Needed"
        checked={result === 'fertile'}
      />
      <ResultCheckbox
        label="Questionable - See Comments"
        checked={result === 'questionable'}
      />
      <ResultCheckbox
        label="Error - See Comments"
        checked={result === 'error'}
      />
    </div>
  </div>
);

interface SampleInformationProps {
  data: ResultsData;
}

const SampleInformation: React.FC<SampleInformationProps> = ({ data }) => (
  <div>
    <h3 className="text-brand-blue-400 text-base font-bold mb-4 pb-1 border-b-2 border-brand-blue-400">
      Sample Information:
    </h3>
    <ul className="space-y-2 text-sm leading-relaxed">
      <li><strong>Collection Date:</strong> {formatCollectionDate(data?.results?.collectionDateTime)}</li>
      <li><strong>Collection Time:</strong> {formatCollectionTime(data?.results?.collectionDateTime)}</li>
      <li><strong>Lab Testing Date:</strong> {formatLabDate(data?.results?.resultsTimeStamp)}</li>
      <li><strong>Lab Testing Time:</strong> {formatLabTime(data?.results?.resultsTimeStamp)}</li>
      <li><strong>Sample Type:</strong> Semen</li>
      <li><strong>Sample Source:</strong> Ejaculate</li>
      <li><strong>Test Performed at:</strong> Androlab Inc</li>
      <li className="pl-28">1450 Summit Avenue, Suite 155</li>
      <li className="pl-28">Oakbrook Terrace, IL 60181</li>
    </ul>
  </div>
);

interface CommentsSectionProps {
  result: string | undefined;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({ result }) => (
  <div>
    <h3 className="text-brand-blue-400 text-base font-bold mb-4 pb-1 border-b-2 border-brand-blue-400">
      Comments:
    </h3>
    <div className="bg-gray-50 p-4 rounded border-l-4 border-brand-blue-400 text-sm leading-relaxed">
      {getCommentForResult(result)}
    </div>
  </div>
);

interface ResultsContentProps {
  data: ResultsData;
}

const ResultsContent: React.FC<ResultsContentProps> = ({ data }) => (
  <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
    <div className="text-end py-6 px-6">
      <button
        onClick={handlePrint}
        className="bg-brand-blue-400 text-white px-5 py-2.5 rounded font-bold text-sm hover:bg-opacity-80 transition-colors duration-200"
      >
        Print Results
      </button>
    </div>

    <div className='print-results-container p-6'>
      <img
        className="mx-auto mt-6 w-auto h-48 md:h-72 object-contain"
        src={getLogoSource(data?.current?.result)}
        alt="Result indicator"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-8">
        <div className="space-y-6">
          <PatientInformation data={data} />
          <ResultCheckboxes result={data?.current?.result} />
        </div>

        <div className="space-y-6">
          <SampleInformation data={data} />
          <CommentsSection result={data?.current?.result} />
        </div>
      </div>

      <div className="mt-8 pt-4 border-t-2 border-brand-blue-400 px-6 pb-6">
        <p className="text-xs text-gray-600 leading-relaxed text-justify">
          Jack At-Home Semen Test performs qualitative semen analysis as a post-vasectomy test. 
          Samples are examined by certified laboratory personnel in our CLIA lab, using 60 high-power 
          fields (hp~ under 10x eye piece X 40x objective for a total magnification of 400x). 
          Comments are given in order to aid the customer and/or provider in ruling out a failed vasectomy. 
          Comments do not replace laboratory results and caution must be given when correlating subsequent specimens. 
          Patients may be referred elsewhere for qualitative semen analysis if needed.
        </p>
      </div>
    </div>
  </div>
);

const renderModalBody = (loading: boolean, error: string | null, data: ResultsData | null) => {
  if (loading) {
    return <LoadingState />;
  }
  
  if (error) {
    return <ErrorState error={error} />;
  }
  
  if (data) {
    return <ResultsContent data={data} />;
  }
  
  return <p className="text-center text-gray-500 py-5">No data available.</p>;
};

const KitResults: React.FC<KitResultsProps> = ({ serialNumber, onClose }) => {
  const [data, setData] = useState<ResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!serialNumber) {
      setError("Serial number is missing.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const resultsData = await fetchResults(serialNumber);
        setData(resultsData);
      } catch (err) {
        console.error('Error fetching results:', err);
        setError(err instanceof Error ? err.message : 'Failed to load results. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [serialNumber]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center p-2">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Test Result Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {renderModalBody(loading, error, data)}
        </div>

        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-right flex-shrink-0">
          <button
            className="close-modal-button"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default KitResults;